/**
 * پنل مدیریت — سامانه ایمیل
 * ==========================
 * چهار بخش: وضعیت و آزمایش اتصال، متن ایمیل‌ها، گزارش ارسال، و ارسال دستی.
 *
 * تنظیمات SMTP خودشان اینجا نیستند: در صفحه‌ی «تنظیمات» گروه «ایمیل»‌اند،
 * چون همان‌جا رمز عبور خودکار رمزنگاری و ماسک می‌شود. اینجا فقط دکمه‌ی
 * میان‌بر و آزمایش اتصال هست تا مدیر بین دو صفحه سرگردان نشود.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Mail, FileText, ListChecks, Send, Plug, RefreshCw, RotateCcw, Eye,
  AlertTriangle, Check, Settings2, ShieldCheck,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Stat, Tag,
} from '../ui'
import { get, post, put, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, faDate, num, asArray } from '../../lib/format'

const TABS: [string, string, any][] = [
  ['overview', 'نمای کلی', Mail],
  ['templates', 'متن ایمیل‌ها', FileText],
  ['messages', 'گزارش ارسال', ListChecks],
  ['send', 'ارسال دستی', Send],
]

const ST_KIND: Record<string, any> = {
  sent: 'up', queued: 'muted', sending: 'info', retry: 'gold', failed: 'down',
}

export default function Email() {
  const [tab, setTab] = useState('overview')
  return (
    <>
      <PageHead title="سامانه ایمیل"
        subtitle="اتصال SMTP، متن همه ایمیل‌ها، گزارش ارسال و ارسال دستی" />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>
            <Icon className="h-3.5 w-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview />}
      {tab === 'templates' && <Templates />}
      {tab === 'messages' && <Messages />}
      {tab === 'send' && <SendPanel />}
    </>
  )
}

/* ═══════════════════════════════════════════════ نمای کلی */
function Overview() {
  const [d, setD] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [to, setTo] = useState('')
  const [sending, setSending] = useState(false)

  const load = () => {
    setLoading(true)
    get('/admin/email/overview').then(setD).catch(() => setD(null)).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const testConn = async () => {
    setTesting(true)
    try {
      const r: any = await post('/admin/email/test-connection')
      r.ok ? toast.ok(r.message) : toast.err(r.message)
    } catch (e: any) { toast.err(e.message) } finally { setTesting(false) }
  }

  const testSend = async () => {
    if (!to.trim()) { toast.err('نشانی ایمیل را وارد کنید'); return }
    setSending(true)
    try {
      const r: any = await post('/admin/email/test-send', { to: to.trim() })
      r.ok ? toast.ok(r.message) : toast.err(r.message)
    } catch (e: any) { toast.err(e.message) } finally { setSending(false) }
  }

  const cfg = d?.config
  const st = d?.stats

  return (
    <>
      {cfg && !cfg.ready && (
        <Panel title="" dense>
          <div className="flex flex-wrap items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-gold" />
            <p className="flex-1 text-xs leading-6 text-slate-300">
              سرویس ایمیل هنوز آماده نیست — {!cfg.enabled ? 'در تنظیمات روشن نشده'
                : !cfg.host ? 'آدرس سرور SMTP خالی است' : 'نشانی فرستنده خالی است'}.
              تا آن‌وقت هیچ ایمیلی صف نمی‌شود.
            </p>
            <Link to="/admin/settings" className="btn-primary !py-2 text-xs">
              <Settings2 className="h-3.5 w-3.5" /> رفتن به تنظیمات
            </Link>
          </div>
        </Panel>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="ارسال موفق (۷ روز)" value={st ? num(st.sent) : '—'} />
        <Stat label="در صف" value={st ? num(st.queued) : '—'} />
        <Stat label="ناموفق" value={st ? num(st.failed) : '—'} />
        <Stat label="ایمیل تأییدنشده" value={d ? num(d.unverifiedUsers) : '—'} />
      </div>

      <Panel title="اتصال SMTP" dense action={<RefreshButton onClick={load} loading={loading} />}>
        <div className="grid gap-3 p-4 text-xs lg:grid-cols-2">
          <Row label="وضعیت" value={
            cfg?.ready ? <Tag kind="up">آماده</Tag> : <Tag kind="down">آماده نیست</Tag>} />
          <Row label="حالت آزمایشی" value={
            cfg?.sandbox ? <Tag kind="gold">روشن — ایمیلی واقعاً فرستاده نمی‌شود</Tag>
                         : <Tag kind="muted">خاموش</Tag>} />
          <Row label="سرور" value={<span className="num" dir="ltr">{cfg?.host || '—'}:{cfg?.port}</span>} />
          <Row label="رمزنگاری" value={<span className="num" dir="ltr">{cfg?.security}</span>} />
          <Row label="فرستنده" value={<span className="num" dir="ltr">{cfg?.from || '—'}</span>} />
          <Row label="رمز عبور" value={cfg?.hasPassword ? <Tag kind="up">ثبت شده</Tag> : <Tag kind="down">ثبت نشده</Tag>} />
          <Row label="اعلان‌های عمومی" value={cfg?.notificationsEnabled ? 'روشن' : 'خاموش'} />
          <Row label="کد یکبارمصرف ایمیلی" value={cfg?.otpEnabled ? 'روشن' : 'خاموش'} />
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-white/[.06] p-4">
          <button onClick={testConn} disabled={testing} className="btn-ghost !py-2 text-xs">
            <Plug className="h-3.5 w-3.5" /> {testing ? 'در حال بررسی…' : 'آزمایش اتصال'}
          </button>
          <input className="input !py-2 num w-full text-xs lg:w-64" dir="ltr"
            placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)} />
          <button onClick={testSend} disabled={sending} className="btn-primary !py-2 text-xs">
            <Send className="h-3.5 w-3.5" /> {sending ? 'در حال ارسال…' : 'ارسال ایمیل آزمایشی'}
          </button>
        </div>
      </Panel>

      <Panel title="آخرین ارسال‌ها" dense>
        {!d?.recent?.length ? (
          <div className="p-4 text-center text-xs text-slate-500">هنوز ایمیلی فرستاده نشده.</div>
        ) : (
          <div className="p-2">
            {asArray(d, 'recent').map((m: any) => (
              <div key={m.id} className="flex flex-wrap items-center gap-3 border-b border-white/[.05] p-3 last:border-0">
                <Tag kind={ST_KIND[m.status] || 'muted'}>{m.statusLabel}</Tag>
                <span className="num text-[11px] text-slate-400" dir="ltr">{m.to}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-slate-200">{m.subject}</span>
                <span className="text-[11px] text-slate-500">{faDate(m.createdAt)}</span>
                {m.error && <span className="num text-[10px] text-down" dir="ltr">{m.error}</span>}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  )
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-white/[.05] bg-white/[.02] px-3 py-2">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200">{value}</span>
    </div>
  )
}

/* ═══════════════════════════════════════════════ متن ایمیل‌ها */
function Templates() {
  const [d, setD] = useState<any>(null)
  const [open, setOpen] = useState<string>('')
  const [draft, setDraft] = useState<any>(null)
  const [preview, setPreview] = useState<string>('')
  const [busy, setBusy] = useState(false)

  const load = () => get('/admin/email/templates').then(setD).catch(() => setD(null))
  useEffect(() => { load() }, [])

  const edit = (t: any) => {
    setOpen(t.event)
    setDraft({ subject: t.subject, body: t.body, cta_label: t.ctaLabel, cta_path: t.ctaPath })
    setPreview('')
  }

  const save = async (event: string) => {
    setBusy(true)
    try {
      await put(`/admin/email/templates/${event}`, draft)
      toast.ok('ذخیره شد'); setOpen(''); load()
    } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
  }

  const reset = async (event: string) => {
    try { await post(`/admin/email/templates/${event}/reset`); toast.ok('به متن پیش‌فرض برگشت'); setOpen(''); load() }
    catch (e: any) { toast.err(e.message) }
  }

  const showPreview = async (event: string) => {
    try {
      const r: any = await post(`/admin/email/templates/${event}/preview`)
      setPreview(r.html || '')
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <>
      <p className="mb-3 text-xs leading-6 text-slate-500">
        متغیرهای قابل استفاده:{' '}
        <span className="num text-brand-300" dir="ltr">
          {(d?.variables || []).map((v: string) => `{${v}}`).join('  ')}
        </span>
      </p>

      {(d?.groups || []).map((g: any) => (
        <Panel key={g.group} title={g.group} dense>
          <div className="p-2">
            {g.items.map((t: any) => (
              <div key={t.event} className="border-b border-white/[.05] last:border-0">
                <div className="flex flex-wrap items-center gap-2 p-3">
                  <span className="min-w-0 flex-1 truncate text-sm text-white">{t.subject}</span>
                  {t.critical && <Tag kind="gold">امنیتی</Tag>}
                  {!t.enabled && <Tag kind="muted">خاموش</Tag>}
                  <span className="text-[11px] text-slate-500">{num(t.sentCount)} ارسال</span>
                  <button onClick={() => (open === t.event ? setOpen('') : edit(t))}
                    className="btn-ghost !px-2 !py-1 text-[11px]">ویرایش</button>
                </div>

                {open === t.event && draft && (
                  <div className="border-t border-white/[.05] bg-white/[.02] p-4">
                    <label className="label">موضوع ایمیل</label>
                    <input className="input mb-3" value={draft.subject}
                      onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
                    <label className="label">متن</label>
                    <textarea className="input mb-3 min-h-[160px] leading-8" value={draft.body}
                      onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
                    <div className="mb-3 grid gap-3 lg:grid-cols-2">
                      <div>
                        <label className="label">متن دکمه (اختیاری)</label>
                        <input className="input" value={draft.cta_label}
                          onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })} />
                      </div>
                      <div>
                        <label className="label">مسیر دکمه</label>
                        <input className="input num" dir="ltr" placeholder="/wallet" value={draft.cta_path}
                          onChange={(e) => setDraft({ ...draft, cta_path: e.target.value })} />
                      </div>
                    </div>
                    <p className="mb-3 text-[11px] leading-6 text-slate-500">
                      خطی به شکل «برچسب: مقدار» در قالب به یک ردیف جدول تبدیل می‌شود.
                      مسیر دکمه باید داخلی باشد و با <span className="num" dir="ltr">/</span> شروع شود.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => save(t.event)} disabled={busy} className="btn-primary !py-2 text-xs">
                        <Check className="h-3.5 w-3.5" /> ذخیره
                      </button>
                      <button onClick={() => showPreview(t.event)} className="btn-ghost !py-2 text-xs">
                        <Eye className="h-3.5 w-3.5" /> پیش‌نمایش
                      </button>
                      <button onClick={() => reset(t.event)} className="btn-ghost !py-2 text-xs">
                        <RotateCcw className="h-3.5 w-3.5" /> بازگشت به پیش‌فرض
                      </button>
                    </div>
                    {preview && (
                      <iframe title="پیش‌نمایش" srcDoc={preview} sandbox=""
                        className="mt-4 h-[460px] w-full rounded-xl border border-white/10 bg-white" />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </>
  )
}

/* ═══════════════════════════════════════════════ گزارش ارسال */
function Messages() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/email/messages${qs({ q, status, page })}`)
      .then((r: any) => { setRows(asArray(r, 'data')); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [q, status, page])

  const retry = async (id: number) => {
    try { await post(`/admin/email/messages/${id}/retry`); toast.ok('به صف بازگشت'); load() }
    catch (e: any) { toast.err(e.message) }
  }

  return (
    <Panel title="گزارش ارسال ایمیل" dense
      action={<RefreshButton onClick={load} loading={loading} />}>
      <Toolbar>
        <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1) }} placeholder="ایمیل یا موضوع…" />
        <Select value={status} onChange={(v) => { setStatus(v); setPage(1) }} options={[
          ['', 'همه وضعیت‌ها'], ['sent', 'ارسال شد'], ['queued', 'در صف'],
          ['retry', 'تلاش مجدد'], ['failed', 'ناموفق'],
        ]} />
      </Toolbar>
      <DataTable rows={rows} loading={loading} minWidth={900}
        empty="ایمیلی ثبت نشده است"
        columns={[
          { key: 'to', header: 'گیرنده',
            cell: (m: any) => <span className="num text-slate-200" dir="ltr">{m.to}</span> },
          { key: 'subject', header: 'موضوع',
            cell: (m: any) => <div className="max-w-[300px] truncate text-slate-300">{m.subject}</div> },
          { key: 'event', header: 'رویداد',
            cell: (m: any) => <span className="num text-[11px] text-slate-500" dir="ltr">{m.event}</span> },
          { key: 'status', header: 'وضعیت',
            cell: (m: any) => (
              <div className="flex flex-col gap-1">
                <Tag kind={ST_KIND[m.status] || 'muted'}>{m.statusLabel}</Tag>
                {m.error && <span className="num text-[10px] text-down" dir="ltr">{m.error}</span>}
              </div>
            ) },
          { key: 'attempts', header: 'تلاش',
            cell: (m: any) => <span className="num text-slate-400">{num(m.attempts)}</span> },
          { key: 'createdAt', header: 'زمان',
            cell: (m: any) => <span className="text-[11px] text-slate-500">{faDate(m.createdAt)}</span> },
          { key: 'act', header: '',
            cell: (m: any) => m.status === 'sent' ? null : (
              <button onClick={() => retry(m.id)} className="btn-ghost !px-2 !py-1 text-[11px]">
                <RefreshCw className="h-3 w-3" /> تلاش مجدد
              </button>
            ) },
        ]} />
      <Pager meta={meta} onPage={setPage} />
    </Panel>
  )
}

/* ═══════════════════════════════════════════════ ارسال دستی */
function SendPanel() {
  const [f, setF] = useState({ to: '', subject: '', body: '', cta_label: '', cta_url: '' })
  const [busy, setBusy] = useState(false)

  const send = async () => {
    setBusy(true)
    try {
      await post('/admin/email/send', f)
      toast.ok('ایمیل به صف رفت')
      setF({ to: '', subject: '', body: '', cta_label: '', cta_url: '' })
    } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
  }

  return (
    <Panel title="ارسال ایمیل دستی" dense>
      <div className="p-4">
        <label className="label">گیرنده</label>
        <input className="input num mb-3" dir="ltr" placeholder="user@example.com"
          value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
        <label className="label">موضوع</label>
        <input className="input mb-3" value={f.subject}
          onChange={(e) => setF({ ...f, subject: e.target.value })} />
        <label className="label">متن</label>
        <textarea className="input mb-3 min-h-[160px] leading-8" value={f.body}
          onChange={(e) => setF({ ...f, body: e.target.value })} />
        <div className="mb-4 grid gap-3 lg:grid-cols-2">
          <div>
            <label className="label">متن دکمه (اختیاری)</label>
            <input className="input" value={f.cta_label}
              onChange={(e) => setF({ ...f, cta_label: e.target.value })} />
          </div>
          <div>
            <label className="label">نشانی دکمه</label>
            <input className="input num" dir="ltr" placeholder="https://arzinoo.com/…"
              value={f.cta_url} onChange={(e) => setF({ ...f, cta_url: e.target.value })} />
          </div>
        </div>
        <button onClick={send} disabled={busy} className="btn-primary !py-2 text-xs">
          <Send className="h-3.5 w-3.5" /> {busy ? 'در حال ارسال…' : 'ارسال'}
        </button>
        <p className="mt-3 flex items-start gap-2 text-[11px] leading-6 text-slate-500">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          ایمیل در همان قالب رسمی سایت فرستاده می‌شود و در گزارش ارسال ثبت می‌گردد.
        </p>
      </div>
    </Panel>
  )
}
